import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { NavBar } from '../components/NavBar';
import { 
  TrendingDown,
  TrendingUp,
  Minus,
  Scale,
  Target,
  Plus,
  Trash2,
  Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { format, parseISO } from 'date-fns';
import StrengthTrends from '../components/strength/StrengthTrends';
import { bodyToUnit, unitToLb, unitLabel } from '../components/strength/units';
import { useWeightUnit } from '../hooks/useWeightUnit';
import { WeightUnitToggle } from '../components/common/WeightUnitToggle';

const LOGO_URL = '/logo.webp';

export const ProgressPage = () => {
  const { api, user, updateProfile } = useAuth();
  const [weightHistory, setWeightHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [newWeight, setNewWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState(user?.target_weight || '');
  const [startingWeight, setStartingWeight] = useState(user?.starting_weight || '');
  const [loading, setLoading] = useState(true);
  const { unit, setUnit } = useWeightUnit();
  const U = unitLabel(unit);
  const show = (lb) => bodyToUnit(lb, unit);
  const toLb = (v) => (v === '' || v == null ? null : unitToLb(parseFloat(v), unit));

  // Goal inputs are edited in the display unit; re-derive whenever source or unit changes.
  useEffect(() => {
    setStartingWeight(bodyToUnit(summary?.starting_weight ?? user?.starting_weight, unit));
    setTargetWeight(bodyToUnit(summary?.target_weight ?? user?.target_weight, unit));
  }, [unit, summary?.starting_weight, summary?.target_weight, user?.starting_weight, user?.target_weight]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [historyRes, summaryRes] = await Promise.all([
        api().get('/progress/weight'),
        api().get('/progress/summary')
      ]);
      setWeightHistory(historyRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('ProgressPage: failed to fetch weight + summary', err);
    }
    setLoading(false);
  };

  const logWeight = async (e) => {
    e.preventDefault();
    if (!newWeight) return;
    
    try {
      await api().post('/progress/weight', { weight: toLb(newWeight) });
      setNewWeight('');
      toast.success('Weight logged!');
      fetchData();
    } catch (err) {
      toast.error('Failed to log weight');
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await api().delete(`/progress/weight/${entryId}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  const saveGoals = async () => {
    try {
      await updateProfile({
        starting_weight: startingWeight ? toLb(startingWeight) : null,
        target_weight: targetWeight ? toLb(targetWeight) : null
      });
      toast.success('Goals saved!');
      fetchData();
    } catch (err) {
      toast.error('Failed to save goals');
    }
  };

  const chartData = weightHistory.map(entry => ({
    date: format(parseISO(entry.date), 'MMM d'),
    weight: show(entry.weight),
    fullDate: entry.date
  }));

  const getTrendIcon = () => {
    if (!summary?.trend) return <Minus className="h-6 w-6" />;
    if (summary.trend === 'losing') return <TrendingDown className="h-6 w-6 text-green-400" />;
    if (summary.trend === 'gaining') return <TrendingUp className="h-6 w-6 text-red-400" />;
    return <Minus className="h-6 w-6 text-yellow-400" />;
  };

  const progressToGoal = () => {
    if (!summary?.starting_weight || !summary?.target_weight || !summary?.current_weight) return 0;
    const totalToLose = summary.starting_weight - summary.target_weight;
    const lost = summary.starting_weight - summary.current_weight;
    return Math.min(Math.max((lost / totalToLose) * 100, 0), 100);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="progress-page">
      <NavBar title="Progress" />

      <main className="container mx-auto px-6 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Weight <span className="text-primary">Progress</span>
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-muted-foreground text-lg">Track your transformation journey</p>
            <WeightUnitToggle unit={unit} onChange={setUnit} testIdPrefix="progress-unit" />
          </div>
        </motion.div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Scale className="h-6 w-6 text-primary" />
              <span className="text-xs text-muted-foreground uppercase">Current</span>
            </div>
            <p className="font-heading text-3xl text-white">{show(summary?.current_weight) || '-'}</p>
            <p className="text-muted-foreground text-sm">{U}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-2">
              {getTrendIcon()}
              <span className="text-xs text-muted-foreground uppercase">Change</span>
            </div>
            <p className={`font-heading text-3xl ${summary?.total_change < 0 ? 'text-green-400' : summary?.total_change > 0 ? 'text-red-400' : 'text-white'}`}>
              {summary?.total_change > 0 ? '+' : ''}{show(summary?.total_change) || 0}
            </p>
            <p className="text-muted-foreground text-sm">{U} total</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="text-xs text-muted-foreground uppercase">Target</span>
            </div>
            <p className="font-heading text-3xl text-white">{show(summary?.target_weight) || '-'}</p>
            <p className="text-muted-foreground text-sm">{U} goal</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-6 w-6 text-primary" />
              <span className="text-xs text-muted-foreground uppercase">Entries</span>
            </div>
            <p className="font-heading text-3xl text-white">{summary?.entries_count || 0}</p>
            <p className="text-muted-foreground text-sm">logged</p>
          </motion.div>
        </div>

        {/* Goal Progress */}
        {summary?.target_weight && summary?.starting_weight && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-card border border-border rounded-2xl p-6 mb-8"
          >
            <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Goal Progress</h3>
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-muted-foreground">Starting: {show(summary.starting_weight)} {U}</span>
              <span className="text-primary font-heading text-lg">{Math.round(progressToGoal())}%</span>
              <span className="text-muted-foreground">Target: {show(summary.target_weight)} {U}</span>
            </div>
            <Progress value={progressToGoal()} className="h-4" />
          </motion.div>
        )}

        {/* Strength Tracker — PRs ribbon + per-exercise 1RM trend chart */}
        <StrengthTrends />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chart */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="lg:col-span-2 bg-card border border-border rounded-2xl p-6"
          >
            <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-6">Weight History</h3>
            {chartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="date" stroke="#A1A1AA" tick={{ fill: '#A1A1AA' }} />
                    <YAxis stroke="#A1A1AA" tick={{ fill: '#A1A1AA' }} domain={unit === 'kg' ? ['dataMin - 2', 'dataMax + 2'] : ['dataMin - 5', 'dataMax + 5']} />
                    <Tooltip 
                      formatter={(v) => [`${v} ${U}`, 'Weight']}
                      contentStyle={{ backgroundColor: '#09090B', border: '1px solid #27272A', borderRadius: '4px' }}
                      labelStyle={{ color: '#FFD700' }}
                    />
                    {summary?.target_weight && (
                      <ReferenceLine y={show(summary.target_weight)} stroke="#22C55E" strokeDasharray="5 5" label={{ value: 'Goal', fill: '#22C55E' }} />
                    )}
                    <Line 
                      type="monotone" 
                      dataKey="weight" 
                      stroke="#FFD700" 
                      strokeWidth={3}
                      dot={{ fill: '#FFD700', strokeWidth: 2 }}
                      activeDot={{ r: 8, fill: '#FFD700' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                <p>No weight entries yet. Start tracking your progress!</p>
              </div>
            )}
          </motion.div>

          {/* Log Weight & Settings */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-6"
          >
            {/* Log Weight */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Log Weight</h3>
              <form onSubmit={logWeight} className="space-y-4">
                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Today's Weight ({U})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newWeight}
                    onChange={(e) => setNewWeight(e.target.value)}
                    placeholder={unit === 'kg' ? 'e.g., 79.5' : 'e.g., 175.5'}
                    className="mt-2 h-12 bg-black border-border focus:border-primary text-white"
                    data-testid="weight-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-primary text-primary-foreground font-heading tracking-wider uppercase"
                  data-testid="log-weight-btn"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Log Weight
                </Button>
              </form>
            </div>

            {/* Goal Settings */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Goal Settings</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Starting Weight ({U})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={startingWeight}
                    onChange={(e) => setStartingWeight(e.target.value)}
                    placeholder="Your starting weight"
                    className="mt-2 h-12 bg-black border-border focus:border-primary text-white"
                    data-testid="starting-weight-input"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm uppercase tracking-wider">Target Weight ({U})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="Your goal weight"
                    className="mt-2 h-12 bg-black border-border focus:border-primary text-white"
                    data-testid="target-weight-input"
                  />
                </div>
                <Button 
                  onClick={saveGoals}
                  variant="outline"
                  className="w-full h-12 border-primary text-primary hover:bg-primary/10 font-heading tracking-wider uppercase"
                  data-testid="save-goals-btn"
                >
                  Save Goals
                </Button>
              </div>
            </div>

            {/* Recent Entries */}
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Recent Entries</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {weightHistory.slice(-10).reverse().map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <p className="text-white font-medium">{show(entry.weight)} {U}</p>
                      <p className="text-muted-foreground text-sm">{format(parseISO(entry.date), 'MMM d, yyyy')}</p>
                    </div>
                    <button 
                      onClick={() => deleteEntry(entry.id)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {weightHistory.length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-4">No entries yet</p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};
